import { useContext, useMemo, useState } from "react";
import { ActividadContext } from "../../../context/ActividadContext";
import { useAuthContext } from "../../../context/authContext";
import { formatMonto } from "../../../utils/rentalCalculations";
import LoadingState from "../../../components/LoadingState/LoadingState";
import { FaBell, FaSearch } from "react-icons/fa";

const TIPOS = {
  COBRO_ALQUILER: "Cobro de alquiler",
  PAGO_ANULADO: "Pago anulado",
  AUMENTO_APLICADO: "Aumento aplicado",
  CONTRATO_CREADO: "Contrato creado",
  CONTRATO_ELIMINADO: "Contrato eliminado",
  LOCADOR_CREADO: "Locador creado",
  LOCADOR_ELIMINADO: "Locador eliminado",
  LOCATARIO_CREADO: "Locatario creado",
  LOCATARIO_ELIMINADO: "Locatario eliminado",
  FIRMA_ACTUALIZADA: "Firma actualizada",
};

const TIPO_COLOR = {
  COBRO_ALQUILER: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  PAGO_ANULADO: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  AUMENTO_APLICADO: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  CONTRATO_CREADO: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
  CONTRATO_ELIMINADO: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  LOCADOR_CREADO: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  LOCADOR_ELIMINADO: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  LOCATARIO_CREADO: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  LOCATARIO_ELIMINADO: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  FIRMA_ACTUALIZADA: "bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300",
};

const tipoColor = (tipo) =>
  TIPO_COLOR[tipo] || "bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300";

const formatFechaHora = (iso) => {
  if (!iso) return "";
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return "";
  return fecha.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function Actividad() {
  const { actividad, initialLoaded, marcarLeida, marcarTodoLeido, actividadNoLeida } =
    useContext(ActividadContext);
  const { user } = useAuthContext();

  const [search, setSearch] = useState("");
  const [tipo, setTipo] = useState("TODOS");

  const visibles = useMemo(() => {
    const s = search.trim().toLowerCase();
    return [...actividad]
      .filter((a) => tipo === "TODOS" || a.tipo === tipo)
      .filter(
        (a) =>
          !s ||
          a.mensaje?.toLowerCase().includes(s) ||
          a.entidadLabel?.toLowerCase().includes(s) ||
          a.autorNombre?.toLowerCase().includes(s)
      )
      .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
  }, [actividad, search, tipo]);

  if (!initialLoaded) return <LoadingState message="Cargando actividad..." />;

  return (
    // El contenedor del dashboard mide 93vh con py-6 (3rem), asi que ese es el
    // alto disponible: lo usamos como minimo para que el estado vacio estire
    // (mismo patron que Notas.jsx).
    <div className="px-4 md:px-6 py-4 space-y-4 flex flex-col min-h-[calc(93vh-3rem)]">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Actividad</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {actividad.length} eventos en total
            {actividadNoLeida.length > 0 && (
              <span className="text-primary font-medium"> &middot; {actividadNoLeida.length} sin leer</span>
            )}
          </p>
        </div>
        {actividadNoLeida.length > 0 && (
          <button
            onClick={marcarTodoLeido}
            className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            Marcar todo como leido
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
        <div className="relative flex-1 min-w-0 sm:max-w-sm">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar por mensaje, propiedad, autor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <select
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
        >
          <option value="TODOS">Todos los tipos</option>
          {Object.entries(TIPOS).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
      </div>

      {/* Listado */}
      {visibles.length === 0 ? (
        <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl shadow-sm py-16 flex flex-col items-center justify-center text-center px-4">
          <FaBell className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400">
            {actividad.length === 0
              ? "Todavia no hay actividad registrada en el sistema."
              : "Ningun evento coincide con los filtros."}
          </p>
        </div>
      ) : (
        <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl shadow-sm divide-y divide-gray-100 dark:divide-gray-700 overflow-hidden">
          {visibles.map((a) => {
            const noLeida = !a.leidoPor?.includes(user?.uid);
            return (
              <div
                key={a.idFirebase}
                onClick={() => noLeida && marcarLeida(a.idFirebase)}
                className={`flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3 ${
                  noLeida ? "bg-primary/5 dark:bg-primary/10 cursor-pointer" : ""
                }`}
              >
                <span
                  className={`shrink-0 w-fit text-xs font-semibold px-2 py-0.5 rounded-full ${tipoColor(
                    a.tipo
                  )}`}
                >
                  {TIPOS[a.tipo] || a.tipo}
                </span>
                <p className="flex-1 text-sm text-gray-700 dark:text-gray-200">
                  {a.mensaje}
                  {(a.monto || a.monto === 0) && (
                    <span className="font-semibold"> &middot; {formatMonto(a.monto, a.moneda)}</span>
                  )}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 shrink-0 whitespace-nowrap">
                  {a.autorNombre || "Sistema"} &middot; {formatFechaHora(a.createdAt)}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Actividad;
